/** @jsxImportSource @emotion/react */
import { useTranslation } from 'react-i18next';
import { ContentFlex } from '../../../components/layouts/ContentFlex';
import { ContentSection } from '../../../components/layouts/ContentSection';
import { SubTitleGroup, SubTitleLayout } from '../../../components/layouts/ContentLayout';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import CustomGrid from '../../../components/grids/CustomGrid';
import { ElmAuthReqVO, Role } from '../../../models/admin/Role';
import { MenuVO } from '../../../models/admin/Menu';
import { useMessageBar } from '../../../hooks/useMessageBar';
import useSessionStore from '../../../stores/useSessionStore';
import { getELMRoles, getRoles, getRolesByUserId } from '../../../apis/admin/Role';
import { getMenuListByAuth } from '../../../apis/admin/MenuApi';
import { SelectionMode } from '@grapecity/wijmo.grid';
import { ApproveRequestPageType } from 'pages/approves/ApproveRequestDetailCase';
import { selectRequestedAuth } from 'apis/approves/approves';
import _ from 'lodash';

/**
 * @desc ELM 권한 결재 요청 목록
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.01.14   jihwan89       ELM 권한요청 시 기 결재 내역 조회 및 밸리데이션 추가
 */

interface Props {
  aprReqId: string;
  requestRoleList?: Role[];
  isReadOnly?: boolean;
}

const AuthRequest = (props: Props, ref) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();

  const [roleListData, setRoleListData] = useState<Role[]>([]);
  const [availableMenuList, setAvailableMenuList] = useState<MenuVO[]>([]);
  const [requestData, setRequestData] = useState<Role[]>([]);
  const gridRef = useRef<any>();
  const [masterData, setMasterData] = useState<ElmAuthReqVO>();
  const [requestedAuthData, setRequestedAuthData] = useState<any>();
  const [selectedAuthName, setSelectedAuthName] = useState('');

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (requestData.length == 0) {
        return openMessageBar({
          type: 'error',
          content: `${t('asst.msg.권한을 선택해주세요.', '권한을 선택해주세요.')}`,
        });
      }
      if (!_.isEmpty(requestedAuthData)) {
        return openMessageBar({
          type: 'error',
          content:
            `${t('com.label.진행중인 결재가 있습니다.', '진행중인 결재가 있습니다.')}` +
            ' ' +
            `${t('com.label.권한', '권한')}` +
            ': ' +
            requestedAuthData?.roleNm,
        });
      }
      return true;
    },

    getBizReqData: (aprReqId: string, reqRsn: string) => {
      const master = {
        ...masterData,
        castType: 'ElmAuthReq',
        aprReqId: aprReqId,
        requestRoleList: requestData,
      };
      return master;
    },
  }));

  const onInitialized = (grid) => {
    gridRef.current = grid;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;
      // grid.startEditing(true);

      if (ht.panel.cellType != 1) return; // 선택된 영역이 셀이 아니면 나가리.
      const item = grid.rows[ht.row].dataItem;
      const binding = ht.panel.columns[ht.col].binding;
      //  fnAvailableMenuList(item.roleCd);

      // 행 클릭 시 체크박스 선택
      if ('detail' === binding) {
        const item = grid.rows[ht.row].dataItem;
        fnAvailableMenuList(item.roleCd);
        showRoleName(item);
      } else {
        const row = grid.rows[ht.row];
        row.isSelected = !row.isSelected;
        const items = gridRef.current.rows.filter((r) => r.isSelected).map((o) => o.dataItem);
        setRequestData(items as Role[]);
        setSelectedAuthName(' ');
        grid.refresh();
      }
    });
    /*
    grid.loadedRows.addHandler(() => {
    });*/
  };

  const showRoleName = (item) => {
    setSelectedAuthName('[' + item.roleNm + '] ');
  };

  useEffect(() => {
    const grid = gridRef.current;

    if (!grid && requestData.length == 0) return;
    //    console.log('autoRowSelected', grid, requestData);
    try {
      requestData?.forEach((r, i) => {
        const itemIdx = grid.rows.findIndex((o) => o.dataItem.roleCd === r.roleCd);
        if (itemIdx > -1) {
          try {
            grid.rows[itemIdx].isSelected = true;
            // grid.refresh();
          } catch (e) {
            /* e; */
          }

          /* if (i == 0) {
            fnAvailableMenuList(r.roleCd);
          }*/
        }
      });

      grid.refresh();
    } catch (e) {
      //console.log('itemIdx', e);
    }
  }, [roleListData, requestData]);

  useEffect(() => {
    fnSearchRole();
    requestedAuth();
    setTimeout(() => {
      autoRowSelected();
    }, 300);
  }, []);

  const requestedAuth = async () => {
    // [25.01.14] jihwan89 - 기존 결재진행중인 권한요청이 있으면 결재요청 불가
    const condition = {
      optValCtn1: ApproveRequestPageType.ELM_AUTH_REQ,
    };
    const requestedAuth = await selectRequestedAuth(condition);
    if (!_.isEmpty(requestedAuth)) {
      setRequestedAuthData({
        aprReqId: requestedAuth.aprReqId,
        roleNm: requestedAuth.optValCtn5,
      });
    }
  };

  const autoRowSelected = () => {
    if (props?.requestRoleList) {
      const rolSows = props.requestRoleList || [];
      const requestAuth = rolSows.map(({ roleCd }) => ({ roleCd }));
      setRequestData(requestAuth as Role[]);
    } else {
      getRolesByUserId().then((result: Role[]) => {
        setRequestData(result);
      });
    }
  };

  const fnSearchRole = useCallback(() => {
    getELMRoles().then((result: Role[]) => {
      if (result && result.length > 0) {
        setRoleListData(result);
      }
    });
  }, []);

  const fnAvailableMenuList = useCallback((params: string) => {
    getMenuListByAuth(params).then((result: MenuVO[]) => {
      setAvailableMenuList(result);
    });
  }, []);

  useEffect(() => {
    const params = requestData.map((o) => o.roleCd);
    fnAvailableMenuList(params.join(','));
  }, [requestData]);

  const authListDefinition = [
    {
      binding: 'roleNm',
      header: String(t('com.label.권한', '권한')),
      width: '*',
      isReadOnly: true,
      align: 'left',
    },
    {
      binding: 'roleDesc',
      header: String(t('com.label.권한 설명', '권한 설명')),
      width: '*',
      isReadOnly: true,
      align: 'left',
    },
    {
      binding: 'detail',
      header: String(t('com.label.상세', '상세')),
      width: 45,
      isReadOnly: true,
      cssClass: 'WijmoPlay',
      cellTemplate: `<Button id="btnDetail"></Button>`,
    },
    {
      binding: 'roleCd',
      visible: false,
    },
  ];

  const authDescDefinition = [
    {
      binding: 'mnuPath',
      header: String(t('com.label.Menu Path', 'Menu Path')),
      width: '*',
      isReadOnly: true,
      align: 'left',
    },
  ];
  return (
    <>
      <ContentFlex>
        <ContentSection className="section width50p marginT0">
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('com.label.권한 목록정보', '권한 목록정보')}</h3>
            </SubTitleGroup>
          </SubTitleLayout>
          {
            <CustomGrid
              layoutDefinition={authListDefinition}
              rowData={roleListData}
              isSelector={true}
              height={300}
              autoCheck={false}
              excludeFilter={['detail']}
              initialized={onInitialized}
              selectionMode={SelectionMode.Row}
              onChangeCheckedItem={(checkedItems) => {
                const requestAuth = checkedItems.map(({ roleCd }) => ({ roleCd }));
                //  console.log('all onChangeCheckedItem', requestAuth);
                setRequestData(requestAuth as Role[]);
                setSelectedAuthName('');
              }}
            />
          }
        </ContentSection>
        <ContentSection className="section width50p marginT0">
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{selectedAuthName + t('com.label.접속 가능 메뉴', '접속 가능 메뉴')}</h3>
            </SubTitleGroup>
          </SubTitleLayout>
          <CustomGrid
            layoutDefinition={authDescDefinition}
            rowData={availableMenuList}
            isSelector={false}
            height={300}
          />
        </ContentSection>
      </ContentFlex>
    </>
  );
};
export default forwardRef(AuthRequest);
